/**
 * Date-based scheduling (1.0.0-rc.10): when a date or date-time in an asset's metadata, ± a number of days, at a
 * time of day in a time zone, comes due. Used by the "Date in metadata" trigger, the "Wait until a date" step and the
 * "Create task" step's due time. Pure and deterministic (Intl only), so the API, the designer's preview and the
 * tests compute the same times.
 *
 * Values:
 *  - a date field holds `YYYY-MM-DD`: it fires on that day (± offsetDays) at `time` in `timezone`;
 *  - a date-time field holds an ISO 8601 instant (`YYYY-MM-DDTHH:MM:SSZ`, or with an offset): with `useFieldTime` it
 *    fires at that instant's own local time (± offsetDays, counted as calendar days in `timezone`, so 18:00 stays
 *    18:00 across a change of summer time); without, on its local day at `time`.
 *
 * Wall times that do not exist (the hour skipped when summer time starts, e.g. 03:30 on the last Sunday of March in
 * Europe/Helsinki) move forward by the gap (04:30); wall times that happen twice (the repeated hour in late October)
 * take the first.
 */

import { timeOfDayTimecode, type Rational } from './time.ts';

export interface DateRule {
  /** Days after (positive) or before (negative) the date. */
  offsetDays: number;
  /** HH:MM, the local time of day in `timezone`. Ignored with useFieldTime for a date-time value. */
  time: string;
  /** A date-time value fires at its own time of day. */
  useFieldTime?: boolean;
  /** IANA time zone, e.g. Europe/Helsinki. */
  timezone: string;
}

export const DEFAULT_SCHEDULE_TIMEZONE = 'Europe/Helsinki';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;
const DAY_MS = 86_400_000;

/** Whether `tz` is a time zone this runtime knows. */
export function isTimeZone(tz: string): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    formatters.set(tz, f);
  }
  return f;
}

export interface WallTime { y: number; mo: number; d: number; h: number; mi: number; s: number }

/** The local wall time of an instant in a time zone. */
export function wallTimeOf(ms: number, tz: string): WallTime {
  const parts = Object.fromEntries(formatter(tz).formatToParts(new Date(ms)).map((p) => [p.type, p.value]));
  return { y: Number(parts.year), mo: Number(parts.month), d: Number(parts.day), h: Number(parts.hour) % 24, mi: Number(parts.minute), s: Number(parts.second) };
}

/** The zone's offset from UTC at an instant, in milliseconds (Helsinki in summer: +3 h). */
export function zoneOffsetMs(ms: number, tz: string): number {
  const w = wallTimeOf(ms, tz);
  return Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s) - Math.floor(ms / 1000) * 1000;
}

/** The instant a local wall time happens in a zone (see the module comment for gaps and overlaps). */
export function zonedTimeToUtc(w: WallTime, tz: string): number {
  const wall = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s);
  const offsets = [...new Set([zoneOffsetMs(wall - DAY_MS, tz), zoneOffsetMs(wall, tz), zoneOffsetMs(wall + DAY_MS, tz)])];
  const valid = offsets.map((o) => wall - o).filter((t) => zoneOffsetMs(t, tz) === wall - t).sort((a, b) => a - b);
  if (valid.length) return valid[0]!;
  // In the gap: the offset before the change, which lands the gap's length later on the clock.
  return wall - zoneOffsetMs(wall - DAY_MS, tz);
}

/** YYYY-MM-DD of a calendar date ± days. */
export function shiftDate(date: string, days: number): string {
  const m = DATE_RE.exec(date);
  if (!m) throw new RangeError(`Not a date: ${date}`);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + days * DAY_MS).toISOString().slice(0, 10);
}

/** The local calendar date of an instant in a zone. */
export function localDate(ms: number, tz: string): string {
  const w = wallTimeOf(ms, tz);
  return `${String(w.y).padStart(4, '0')}-${String(w.mo).padStart(2, '0')}-${String(w.d).padStart(2, '0')}`;
}

export type DateValue = { kind: 'date'; date: string } | { kind: 'datetime'; ms: number };

/** A stored value as a date or an instant; null for anything else (empty, malformed, an impossible date). */
export function parseDateValue(value: unknown): DateValue | null {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  const d = DATE_RE.exec(v);
  if (d) {
    const ms = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]));
    return new Date(ms).toISOString().slice(0, 10) === v ? { kind: 'date', date: v } : null;
  }
  if (DATETIME_RE.test(v)) {
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? null : { kind: 'datetime', ms };
  }
  return null;
}

/** A date-time value in the stored form: UTC, whole seconds (`2026-10-20T15:00:00Z`); null when it is not one. */
export function normalizeDateTime(value: unknown): string | null {
  const p = parseDateValue(value);
  if (!p || p.kind !== 'datetime') return null;
  return new Date(Math.floor(p.ms / 1000) * 1000).toISOString().replace('.000Z', 'Z');
}

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** When a value comes due under a rule, as epoch milliseconds; null when the value is empty or not a date. */
export function firingTime(value: unknown, rule: DateRule): number | null {
  const v = parseDateValue(value);
  if (!v) return null;
  const t = TIME_RE.exec(rule.time) ?? TIME_RE.exec('09:00')!;
  const at = (date: string, h: number, mi: number, s = 0) => {
    const m = DATE_RE.exec(date)!;
    return zonedTimeToUtc({ y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]), h, mi, s }, rule.timezone);
  };
  if (v.kind === 'date') return at(shiftDate(v.date, rule.offsetDays), Number(t[1]), Number(t[2]));
  const day = shiftDate(localDate(v.ms, rule.timezone), rule.offsetDays);
  if (rule.useFieldTime) {
    const w = wallTimeOf(v.ms, rule.timezone);
    return at(day, w.h, w.mi, w.s);
  }
  return at(day, Number(t[1]), Number(t[2]));
}

/**
 * Stored values whose firing time can fall in (fromMs, toMs]: a text range [lo, hi) over the stored strings (dates
 * and UTC date-times sort as text). Wide by two days on each side; the caller computes the exact time of each.
 */
export function valueRange(fromMs: number, toMs: number, rule: Pick<DateRule, 'offsetDays' | 'timezone'>): { lo: string; hi: string } {
  return {
    lo: shiftDate(localDate(fromMs, rule.timezone), -rule.offsetDays - 2),
    hi: shiftDate(localDate(toMs, rule.timezone), -rule.offsetDays + 3),
  };
}

export type FiringClass = 'due' | 'late' | 'missed' | 'past' | 'future';

/**
 * How a sweep at `nowMs` treats a firing time:
 *  - future: not yet;
 *  - past: the time had already passed when the date was set or the flow was published (`knownSinceMs`): fires only
 *    with "also for dates already passed";
 *  - missed: due, but more than the catch-up window ago (PRISM was down): skipped and logged;
 *  - late: due within the window, more than `lateAfterMs` ago (a catch-up);
 *  - due: on time.
 */
export function classifyFiring(firingMs: number, nowMs: number, opts: { knownSinceMs: number; catchUpMs: number; lateAfterMs: number }): FiringClass {
  if (firingMs > nowMs) return 'future';
  if (firingMs < opts.knownSinceMs) return 'past';
  const late = nowMs - firingMs;
  if (late > opts.catchUpMs) return 'missed';
  return late > opts.lateAfterMs ? 'late' : 'due';
}

/** "2 days before Air date at 09:00 (Europe/Helsinki)", for the designer. */
export function describeDateRule(rule: DateRule, field: string): string {
  const n = Math.abs(rule.offsetDays);
  const when = rule.offsetDays === 0 ? `On ${field}` : `${n} day${n === 1 ? '' : 's'} ${rule.offsetDays < 0 ? 'before' : 'after'} ${field}`;
  return `${when} ${rule.useFieldTime ? 'at its own time' : `at ${rule.time}`} (${rule.timezone})`;
}

/** "2026-10-20 09:00 (Europe/Helsinki)", for logs and previews. */
export function formatInZone(ms: number, tz: string): string {
  const w = wallTimeOf(ms, tz);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${w.y}-${p(w.mo)}-${p(w.d)} ${p(w.h)}:${p(w.mi)} (${tz})`;
}

/** 1.9.1: a live recording's title stamp, "2026-11-02 16:05", in the workspace's time zone. */
export function liveTitleStamp(ms: number, tz: string): string {
  const w = wallTimeOf(ms, tz);
  return `${localDate(ms, tz)} ${String(w.h).padStart(2, '0')}:${String(w.mi).padStart(2, '0')}`;
}

/**
 * 1.9.1: time-of-day timecode, the clock time of an instant in a zone as a start timecode at a frame rate (snapped to
 * the nearest frame; drop frame at 29.97 and 59.94). It is the start only: the recording counts on from it, so a
 * summer-time change during the recording does not make it jump.
 */
export function wallClockTimecode(ms: number, tz: string, rate: Rational): string {
  const w = wallTimeOf(ms, tz);
  const fraction = (((ms % 1000) + 1000) % 1000) / 1000;
  return timeOfDayTimecode(w.h * 3600 + w.mi * 60 + w.s + fraction, rate);
}
